import { useState, useEffect } from 'react';
import { dynamicFirebaseService } from '../services/dynamicFirebaseService';
import { ClientConfiguration } from '../types/auth';

export interface UseClientConfigurationResult {
  configuration: ClientConfiguration | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for fetching and caching client-specific configuration
 */
export const useClientConfiguration = (clientId: string | null): UseClientConfigurationResult => {
  const [configuration, setConfiguration] = useState<ClientConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clear previous state when clientId changes
    setConfiguration(null);
    setError(null);

    if (!clientId) {
      setIsLoading(false);
      return;
    }

    const fetchClientConfiguration = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Always ensure Firebase is initialized - the service has its own internal caching
        // This is necessary because the service singleton might have lost its state on page reload
        const cacheKey = `client_config_${clientId}`;
        
        // Initialize Firebase - it will fetch config from backend if not already cached in service
        const configuration = await dynamicFirebaseService.initializeWithClientId(clientId);
        
        setConfiguration(configuration);
        
        // Cache configuration for session duration (update cache with fresh data)
        sessionStorage.setItem(cacheKey, JSON.stringify(configuration));
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'unsupportedClient.errors.configLoadFailed';
        setError(errorMessage);
        setConfiguration(null);
        
        // Remove any corrupted cache entry
        const cacheKey = `client_config_${clientId}`;
        sessionStorage.removeItem(cacheKey);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientConfiguration();
  }, [clientId]);

  // Derived rather than returned raw. isLoading starts false and is only set true inside the effect
  // above, so on the render immediately after clientId resolves a consumer sees a clientId, no
  // configuration and isLoading false - and reasonably concludes the fetch failed. That is what put
  // "Failed to load client configuration" on screen for a moment on every single page load.
  //
  // Having a clientId but neither a configuration nor an error simply means the request has not
  // finished, so say so.
  const isResolving = isLoading || (Boolean(clientId) && configuration === null && error === null);

  return {
    configuration,
    isLoading: isResolving,
    error
  };
};